import type {
  BookingDto,
  CreateBookingInput,
  CreateBookingResult,
  DeviceDto,
  DeviceNextBooking,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Pure decisions for the bookings screens and the Device List's attention
 * surface (WAVE2-DESIGN §1). Kept free of React and i18n so the suite can pin
 * them without a DOM: what counts as imminent, what counts as overdue, and how
 * the board groups are exactly the rules staff will argue about at the counter.
 */

/** Mirrors the server's grace: BOOKED past startsAt by more than this nags. */
export const IMMINENT_WINDOW_MS = 15 * 60_000;

/** Local wall-clock HH:mm — what "Reserved 18:00" means to the person standing there. */
export function timeOfDay(iso: string): string {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export type BookingAttention = 'imminent' | 'overdue' | null;

/**
 * Whether a tile's next booking deserves the banner: starting within the
 * 15-minute window, or already past its start. Anything further out shows on
 * the ribbon only — the banner is for "someone should act now".
 */
export function bookingAttention(nextBooking: DeviceNextBooking, now: number): BookingAttention {
  const startsAt = new Date(nextBooking.startsAt).getTime();
  if (startsAt <= now) return 'overdue';
  if (startsAt - now <= IMMINENT_WINDOW_MS) return 'imminent';
  return null;
}

export interface BannerItem {
  deviceId: string;
  deviceName: string;
  booking: DeviceNextBooking;
  attention: Exclude<BookingAttention, null>;
}

/**
 * The ≤15-min/overdue banner rows, dismissed ones filtered out. Dismissal is
 * per BOOKING id, not per device: resolving one booking must not silence the
 * next party's warning on the same machine.
 */
export function bannerItems(
  devices: readonly DeviceDto[],
  now: number,
  dismissedBookingIds: ReadonlySet<string>,
): BannerItem[] {
  const items: BannerItem[] = [];
  for (const device of devices) {
    const booking = device.nextBooking;
    if (!booking || dismissedBookingIds.has(booking.id)) continue;
    const attention = bookingAttention(booking, now);
    if (attention) items.push({ deviceId: device.id, deviceName: device.name, booking, attention });
  }
  // Most urgent first: overdue ahead of imminent, then by start time.
  return items.sort((a, b) =>
    a.attention === b.attention
      ? a.booking.startsAt.localeCompare(b.booking.startsAt)
      : a.attention === 'overdue'
        ? -1
        : 1,
  );
}

/**
 * Whether starting a walk-in on this device deserves the soft confirm: there
 * is an upcoming booking (the 2h embed window) and the machine is otherwise
 * free to take one. Busy machines never reach the start flow.
 */
export function shouldConfirmWalkIn(device: DeviceDto): boolean {
  return device.nextBooking != null && device.currentSession == null;
}

/** The party a booking names — account name wins over walk-in, like Tab. */
export function partyName(booking: Pick<BookingDto, 'customerName' | 'visitorName'>): string {
  return booking.customerName ?? booking.visitorName ?? 'Guest';
}

export interface BookingHourGroup {
  /** Local wall-clock hour label, e.g. "18:00". */
  hour: string;
  bookings: BookingDto[];
}

/**
 * The board's time grouping: rows bucketed by their local starting hour, in
 * the order the (already startsAt-sorted) list arrives.
 */
export function groupByHour(bookings: readonly BookingDto[]): BookingHourGroup[] {
  const groups: BookingHourGroup[] = [];
  for (const booking of bookings) {
    const hour = `${String(new Date(booking.startsAt).getHours()).padStart(2, '0')}:00`;
    const last = groups[groups.length - 1];
    if (last && last.hour === hour) last.bookings.push(booking);
    else groups.push({ hour, bookings: [booking] });
  }
  return groups;
}

/* -------------------------------------------------------------------------- */
/* Bulk reserve (wave 3 #12)                                                  */
/* -------------------------------------------------------------------------- */

export interface BulkBookingDevice {
  id: string;
  name: string;
}

export interface BulkBookingOutcome {
  deviceId: string;
  deviceName: string;
  ok: boolean;
  /** ApiError code on a refusal (`booking_overlap`, ...), else null. */
  code: string | null;
  message: string | null;
}

export interface BulkBookingResult {
  outcomes: BulkBookingOutcome[];
  /** Non-fatal warnings from successful creates (`live_session_overlap`). */
  warnings: string[];
  failedCount: number;
}

/**
 * Reserve several devices in one go: a client-side loop over the existing
 * `POST /bookings` — no bulk endpoint exists, and the plan keeps it that way.
 *
 * Two rules, both load-bearing:
 * - devices are visited IN TURN and a refusal never aborts the rest: five
 *   parties booked and one clash must read as "5 booked, PC-3 clashed", not as
 *   an exception that hides the five;
 * - every device gets its own outcome row, success or failure — a partial
 *   failure summarised as one grey toast is a swallowed failure.
 */
export async function createBookingsForDevices(
  devices: readonly BulkBookingDevice[],
  base: Omit<CreateBookingInput, 'deviceId'>,
): Promise<BulkBookingResult> {
  const outcomes: BulkBookingOutcome[] = [];
  const warnings: string[] = [];

  for (const device of devices) {
    try {
      const result = await api.post<CreateBookingResult>('/bookings', {
        ...base,
        deviceId: device.id,
      });
      warnings.push(...result.warnings);
      outcomes.push({
        deviceId: device.id,
        deviceName: device.name,
        ok: true,
        code: null,
        message: null,
      });
    } catch (caught) {
      outcomes.push({
        deviceId: device.id,
        deviceName: device.name,
        ok: false,
        code: caught instanceof ApiError ? caught.code : null,
        message: caught instanceof Error ? caught.message : null,
      });
    }
  }

  return { outcomes, warnings, failedCount: outcomes.filter((outcome) => !outcome.ok).length };
}
